import { useEffect, useRef, useState } from "react";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { canManageMembers } from "../lib/access";
import { ApiError } from "../lib/api/client";
import { createMember, deleteMember, getMemberProfile, listMembers, listPlans, updateMember } from "../lib/api/endpoints";
import { memberEnrollmentTypeLabel, memberGenderLabel } from "../lib/enums";
import { useSession } from "../lib/session/SessionContext";
import { C } from "../theme";
import type { CreateMemberRequest, MemberDto, MemberProfileDto, PlanDto } from "../types";
import { Button } from "./Button";
import { Card } from "./Card";
import { ConfirmDialog } from "./ConfirmDialog";
import { FieldInput } from "./Field";
import { Label } from "./Label";
import { MemberDetailPanel } from "./MemberDetailPanel";
import { MemberForm } from "./MemberForm";

const PAGE_SIZE = 20;
const PLANS_PAGE_SIZE = 100;

function memberCountLabel(totalCount: number): string {
  return `${totalCount} ${totalCount === 1 ? "aluno cadastrado" : "alunos cadastrados"}`;
}

function formatCpf(cpf: string): string {
  return cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
}

function formatDateOfBirth(value: string | null): string {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  return year && month && day ? `${day}/${month}/${year}` : "";
}

export function MembersTab() {
  const { accessToken, activeTenant } = useSession();
  const token = accessToken ?? "";
  const canManage = canManageMembers(activeTenant?.roleSlug ?? null);
  const submittingRef = useRef(false);
  const deletingRef = useRef(false);
  const [members, setMembers] = useState<MemberDto[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [plans, setPlans] = useState<PlanDto[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [formOpen, setFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<MemberDto | null>(null);
  const [editingProfile, setEditingProfile] = useState<MemberProfileDto | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileReloadKey, setProfileReloadKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MemberDto | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [detailMember, setDetailMember] = useState<MemberDto | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadError(null);
    listMembers(token, { page: 1, pageSize: PAGE_SIZE, search })
      .then(result => {
        if (cancelled) return;
        setMembers(result.items);
        setTotalCount(result.totalCount);
      })
      .catch(error => {
        if (cancelled) return;
        setMembers([]);
        setLoadError(error instanceof ApiError ? error.detail ?? "Falha ao carregar alunos" : "Falha ao carregar alunos");
      });
    return () => { cancelled = true; };
  }, [token, reloadKey, search]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    listPlans(token, { page: 1, pageSize: PLANS_PAGE_SIZE, isActive: true })
      .then(result => {
        if (!cancelled) setPlans(result.items);
      })
      .catch(() => {
        if (!cancelled) setPlans([]);
      });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    const timeout = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    if (!formOpen || !editingMember || !token) return;
    let cancelled = false;
    setProfileLoading(true);
    setProfileError(null);
    setEditingProfile(null);
    getMemberProfile(token, editingMember.id)
      .then(profile => {
        if (cancelled) return;
        setEditingProfile(profile);
        setProfileLoading(false);
      })
      .catch(error => {
        if (cancelled) return;
        setProfileError(error instanceof ApiError ? error.detail ?? "Falha ao carregar o perfil do aluno" : "Falha ao carregar o perfil do aluno");
        setProfileLoading(false);
      });
    return () => { cancelled = true; };
  }, [formOpen, editingMember, token, profileReloadKey]);

  const openCreateForm = () => {
    setSubmitError(null);
    setActionError(null);
    setSuccessMessage(null);
    setEditingMember(null);
    setEditingProfile(null);
    setProfileError(null);
    setFormOpen(true);
  };

  const openEditForm = (member: MemberDto) => {
    setSubmitError(null);
    setActionError(null);
    setSuccessMessage(null);
    setEditingMember(member);
    setEditingProfile(null);
    setProfileError(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    if (submittingRef.current) return;
    setFormOpen(false);
    setSubmitError(null);
    setEditingMember(null);
    setEditingProfile(null);
    setProfileError(null);
    setProfileLoading(false);
  };

  const handleSubmit = async (payload: CreateMemberRequest) => {
    if (!token || submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const saved = editingMember
        ? await updateMember(token, editingMember.id, payload)
        : await createMember(token, payload);
      setMembers(current => [saved, ...(current ?? []).filter(member => member.id !== saved.id)]);
      setTotalCount(current => editingMember ? current : current + 1);
      setSearchInput("");
      setSearch("");
      setFormOpen(false);
      setEditingMember(null);
      setEditingProfile(null);
      setSuccessMessage(editingMember ? "Alterações salvas com sucesso." : "Aluno cadastrado com sucesso.");
    } catch (error) {
      setSubmitError(error instanceof ApiError ? error.detail ?? error.message : "Falha ao salvar aluno");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!token || !pendingDelete || deletingRef.current) return;
    const member = pendingDelete;
    deletingRef.current = true;
    setDeleting(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      await deleteMember(token, member.id);
      setMembers(current => current?.filter(item => item.id !== member.id) ?? []);
      setTotalCount(current => Math.max(0, current - 1));
      setPendingDelete(null);
      setSuccessMessage("Aluno removido com sucesso.");
    } catch (error) {
      setActionError(error instanceof ApiError ? error.detail ?? error.message : "Falha ao remover aluno");
      setPendingDelete(null);
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };

  return (
    <>
      <Card>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div>
            <Label>Alunos</Label>
            <div style={{ color: C.light, fontSize: 12, marginTop: 3 }}>{memberCountLabel(totalCount)}</div>
          </div>
          {canManage && <Button onClick={openCreateForm}>+ Cadastrar aluno</Button>}
        </div>

        <div style={{ width: "100%", maxWidth: 520, marginTop: 14 }}>
          <FieldInput
            type="search"
            aria-label="Buscar aluno"
            value={searchInput}
            onChange={event => setSearchInput(event.target.value)}
            placeholder="Buscar aluno por nome, matrícula, CPF, e-mail ou telefone"
          />
        </div>

        {successMessage && (
          <div role="status" style={{ color: C.ink, background: C.limeSoft, borderRadius: 8, padding: 10, marginTop: 14, fontSize: 12.5 }}>
            {successMessage}
          </div>
        )}

        {loadError && (
          <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, marginTop: 14, fontSize: 12.5 }}>
            {loadError}
            <Button variant="ghost" onClick={() => setReloadKey(key => key + 1)} style={{ marginLeft: 10 }}>Tentar novamente</Button>
          </div>
        )}

        {actionError && (
          <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, marginTop: 14, fontSize: 12.5 }}>
            {actionError}
          </div>
        )}

        {members === null && <div style={{ color: C.light, fontSize: 13, padding: "24px 0" }}>Carregando alunos...</div>}
        {members?.length === 0 && !loadError && (
          <div style={{ color: C.light, fontSize: 13, padding: "24px 0" }}>
            {search ? "Nenhum aluno encontrado para esta busca." : "Nenhum aluno cadastrado ainda."}
          </div>
        )}
        {members && members.length > 0 && (
          <div style={{ overflowX: "auto", marginTop: 14 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 780, fontSize: 12.5 }}>
              <thead>
                <tr style={{ textAlign: "left", color: C.light }}>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Aluno</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Matrícula</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>CPF</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}` }}>Contato</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right" }}>Detalhes</th>
                  {canManage && <th style={{ padding: "8px 10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right" }}>Ações</th>}
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr key={member.id}>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                      <div style={{ color: C.ink, fontWeight: 600 }}>{member.name}</div>
                      {(member.dateOfBirth || member.gender) && (
                        <div style={{ fontSize: 11, color: C.light, marginTop: 2 }}>
                          {[member.dateOfBirth && `Nascimento ${formatDateOfBirth(member.dateOfBirth)}`, member.gender && memberGenderLabel(member.gender)]
                            .filter(Boolean)
                            .join(" • ")}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}` }}>
                      <div style={{ color: C.gray }}>{member.registrationNumber}</div>
                      {member.enrollment && (
                        <div style={{ fontSize: 11, color: C.light, marginTop: 2 }}>
                          {member.enrollment.planName} • {memberEnrollmentTypeLabel(member.enrollment.type)}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>{formatCpf(member.cpf)}</td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, color: C.gray }}>
                      {member.email ?? member.phone ?? "—"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right", whiteSpace: "nowrap" }}>
                      <Button
                        variant="ghost"
                        ariaLabel={`Ver detalhes de ${member.name}`}
                        title={`Ver detalhes de ${member.name}`}
                        onClick={() => {
                          setActionError(null);
                          setSuccessMessage(null);
                          setDetailMember(member);
                        }}
                        style={{ color: C.gray, padding: 6, textDecoration: "none", display: "inline-flex" }}
                      >
                        <Eye size={16} aria-hidden="true" />
                      </Button>
                    </td>
                    {canManage && (
                      <td style={{ padding: "10px", borderBottom: `1px solid ${C.faint}`, textAlign: "right", whiteSpace: "nowrap" }}>
                        <Button
                          variant="ghost"
                          ariaLabel={`Editar ${member.name}`}
                          title={`Editar ${member.name}`}
                          onClick={() => openEditForm(member)}
                          style={{ color: C.gray, padding: 6, textDecoration: "none", display: "inline-flex", marginRight: 4 }}
                        >
                          <Pencil size={15} aria-hidden="true" />
                        </Button>
                        <Button
                          variant="ghost"
                          ariaLabel={`Excluir ${member.name}`}
                          title={`Excluir ${member.name}`}
                          onClick={() => {
                            setActionError(null);
                            setSuccessMessage(null);
                            setPendingDelete(member);
                          }}
                          style={{ color: C.red, padding: 6, textDecoration: "none", display: "inline-flex" }}
                        >
                          <Trash2 size={16} aria-hidden="true" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {formOpen && (
        <MemberForm
          key={editingMember ? `${editingMember.id}-${editingProfile ? "ready" : "loading"}` : "new"}
          submitting={submitting}
          submitError={submitError}
          plans={plans}
          initial={editingProfile}
          editing={Boolean(editingMember)}
          loading={Boolean(editingMember) && profileLoading}
          loadError={profileError}
          onRetryLoad={() => setProfileReloadKey(key => key + 1)}
          onSubmit={handleSubmit}
          onCancel={closeForm}
        />
      )}

      {pendingDelete && (
        <ConfirmDialog
          title="Excluir aluno"
          confirmLabel="Excluir"
          busy={deleting}
          busyLabel="Excluindo..."
          onConfirm={handleDelete}
          onCancel={() => setPendingDelete(null)}
        >
          Tem certeza que deseja remover <b style={{ color: C.ink }}>&quot;{pendingDelete.name}&quot;</b>? O aluno deixará de aparecer nas buscas e listagens.
        </ConfirmDialog>
      )}

      {detailMember && <MemberDetailPanel member={detailMember} onClose={() => setDetailMember(null)} />}
    </>
  );
}